import { useState, useEffect, useMemo } from 'react';
import { Alert, Button, Layout, Row } from 'antd';
import { DragDropContext } from 'react-beautiful-dnd';

import dndStructure from './initStructure/dndStructure';
import sampleCourses from './initStructure/SampleCourses';
import ListOfCourses from './ListOfCourses';
import SelectedCourses from './SelectedCourses';
import Statistics from './Statistics';
import AddEditCourseModalForm from './AddEditCourseModalForm';

import {checkColumnStructure, checkCoursesStructure, isJSONFromSparta, parseSpartaJSONToValid} from './utils';

const { Content } = Layout;

const Curricula = ({ rsSkillsGroup, rsSpecificKnowledge, rsSpecificSkills, rsProfiles, showMessage }) => {
  const [courses, setCourses] = useState({});
  const [columns, setColumns] = useState(dndStructure.columns);
  const [error, setError] = useState(null);
  const [draggedCourse, setDraggedCourse] = useState(null);
  const [sampleCoursesLoaded, setSampleCoursesLoaded] = useState(false);
  const [addModalData, setAddModalData] = useState(null);
  const [modalCourseId, setModalCourseId] = useState(null);

  const initialColumns = useMemo(() => JSON.parse(JSON.stringify(dndStructure.columns)), []);

  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  function openAddModal(courseId) {
    setModalCourseId(courseId);
    setAddModalData(courseId != null ? courses[courseId] : 'new');
  }

  function closeAddModal() {
    setModalCourseId(null);
    setAddModalData(null);
  }

  function createCourseDnDId(name, prefix, pool) {
    let base = `${prefix}_${name.replace(/ /g, '_').toLowerCase()}`;
    let counter = 0;
    while (pool.includes(`${base}_${counter}`)) counter++;
    return `${base}_${counter}`;
  }

  const submitModal = (inputData) => {
    if (modalCourseId == null) {
      addCustomCourse(inputData);
    } else {
      updateCourse(inputData);
    }
    closeAddModal();
  };

  function addCustomCourse(input) {
    const newCourse = { ...input };
    const newCourses = { ...courses };
    const newCols = { ...columns };
    delete newCourse.currentTopic;
    delete newCourse.currentTopicPer;
    const id = createCourseDnDId(newCourse.name, 'user', Object.keys(newCourses));
    newCourse.id = id;
    const pos = `${newCourse.type}-${newCourse.semester.charAt(0)}`;
    newCols[pos].courseIds.push(id);
    newCourses[id] = newCourse;
    setCourses(newCourses);
    setColumns(newCols);
  }

  function updateCourse(input) {
    const oldId = modalCourseId;
    const newCourse = { ...input };
    const newCourses = { ...courses };
    const newCols = { ...columns };
    const id = createCourseDnDId(newCourse.name, 'user', Object.keys(newCourses));
    newCourse.id = id;
    delete newCourse.currentTopic;
    delete newCourse.currentTopicPer;
    const oldPos = Object.keys(columns).find((k) => columns[k].courseIds.includes(oldId));
    const newPos = isNaN(oldPos.charAt(0)) ? `${newCourse.type}-${newCourse.semester.charAt(0)}` : `${oldPos.charAt(0)}-${newCourse.semester.charAt(0)}`;

    delete newCourses[oldId];
    newCourses[id] = newCourse;

    const idx = newCols[oldPos].courseIds.indexOf(oldId);
    if (newPos === oldPos) {
      newCols[oldPos].courseIds[idx] = id;
    } else {
      newCols[oldPos].courseIds.splice(idx, 1);
      newCols[newPos].courseIds.push(id);
    }

    setCourses(newCourses);
    setColumns(newCols);
  }

  function removeForeverCourse({ id, position }) {
    const newCourses = { ...courses };
    const newCols = { ...columns };
    newCols[position].courseIds = newCols[position].courseIds.filter((cid) => cid !== id);
    delete newCourses[id];
    setCourses(newCourses);
    setColumns(newCols);
  }

  function removeCourse({ id, type, semester, position }) {
    const newCols = { ...columns };
    const oldPos = position;
    const newPos = `${type}-${semester.charAt(0)}`;
    newCols[oldPos].courseIds = newCols[oldPos].courseIds.filter((cid) => cid !== id);
    newCols[newPos].courseIds.push(id);
    setColumns(newCols);
  }

  function importData(input) {
    try {
      const parsed = JSON.parse(input);
      if (!parsed.courses) {
        setError({ message: 'Wrong input file format!' });
        return;
      }

      if (isJSONFromSparta(parsed.courses)) {
        parsed.courses = parseSpartaJSONToValid(parsed);
        showMessage({
          type: 'warning',
          content: 'Wrong file structure. Please verify import results.',
          duration: 5
        });
      }

      if (!checkColumnStructure(parsed.courses, dndStructure.columns) || !checkCoursesStructure(parsed.courses)) {
        setError({ message: 'Wrong input file format!' });
        return;
      }
      const newCourses = { ...courses };
      const newCols = { ...columns };
      parsed.courses.forEach((course) => {
        const id = createCourseDnDId(course.name, 'import', Object.keys(newCourses));
        course.id = id;
        newCols[course.position].courseIds.push(id);
        delete course.position;
        newCourses[id] = course;
      });
      setCourses(newCourses);
      setColumns(newCols);
    } catch (e) {
      setError({ message: 'Sorry, something went wrong!' });
    }
  }

  function getCoursePosition(course) {
    if (course.type === 'voluntary' && course.semester === 'summer') return 'voluntary-s';
    if (course.type === 'voluntary' && course.semester === 'winter') return 'voluntary-w';
    if (course.type === 'mandatory' && course.semester === 'summer') return 'mandatory-s';
    if (course.type === 'mandatory' && course.semester === 'winter') return 'mandatory-w';
    if (course.type === 'custom' && course.semester === 'summer') return 'custom-s';
    if (course.type === 'custom' && course.semester === 'winter') return 'custom-w';
    return null;
  }

  function loadSamples() {
    if (!sampleCourses.length) {
      setError({ message: 'There are no sample courses available!' });
      return;
    }
    const sorted = [...sampleCourses].sort((a, b) => a.name.localeCompare(b.name));
    const newCourses = { ...courses };
    const newCols = { ...columns };
    sorted.forEach((course) => {
      const id = createCourseDnDId(course.name, 'sample', Object.keys(newCourses));
      course.id = id;
      const pos = getCoursePosition(course);
      if (!pos) return;
      newCols[pos].courseIds.push(id);
      newCourses[id] = course;
    });
    setCourses(newCourses);
    setColumns(newCols);
    setSampleCoursesLoaded(true);
  }

  function onDragStart(start) {
    setDraggedCourse(start.draggableId);
  }

  function onDragEnd(result) {
    const { destination, source, draggableId } = result;
    if (!destination) {
      setDraggedCourse(null);
      return;
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      setDraggedCourse(null);
      return;
    }

    const startCol = columns[source.droppableId];
    const endCol = columns[destination.droppableId];

    if (startCol === endCol) {
      const ids = Array.from(startCol.courseIds);
      ids.splice(source.index, 1);
      ids.splice(destination.index, 0, draggableId);
      const updated = { ...startCol, courseIds: ids };
      setColumns((prev) => ({
        ...prev,
        [updated.id]: updated,
      }));
    } else {
      const startIds = Array.from(startCol.courseIds);
      startIds.splice(source.index, 1);

      const finishIds = Array.from(endCol.courseIds);
      finishIds.splice(destination.index, 0, draggableId);

      setColumns((prev) => ({
        ...prev,
        [startCol.id]: { ...startCol, courseIds: startIds },
        [endCol.id]: { ...endCol, courseIds: finishIds },
      }));
    }

    setDraggedCourse(null);
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Alert message={error.message} type="error" showIcon />
        <Button type="link" onClick={() => setError(null)}>
          Go back
        </Button>
      </div>
    );
  }

  return (
    <Layout className="csprofiler-curricula">
      <Content className="structure-content">
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <Row gutter={16}>
            <ListOfCourses
              columns={columns}
              courses={courses}
              draggedCourse={draggedCourse}
              sampleCoursesLoaded={sampleCoursesLoaded}
              addCustomCourse={addCustomCourse}
              removeCourse={removeForeverCourse}
              loadSamples={loadSamples}
              rsSkillsGroup={rsSkillsGroup}
              openAddModal={openAddModal}
            />
            <SelectedCourses
              columns={columns}
              courses={courses}
              draggedCourse={draggedCourse}
              removeCourse={removeCourse}
              importData={importData}
              rsSkillsGroup={rsSkillsGroup}
              openAddModal={openAddModal}
            />
            <Statistics columns={columns} courses={courses} rsSkillsGroup={rsSkillsGroup} rsProfiles={rsProfiles} />
          </Row>
        </DragDropContext>
      </Content>
      <AddEditCourseModalForm
        editRecord={addModalData}
        saveLocally={submitModal}
        onCancel={closeAddModal}
        refresh={closeAddModal}
        showMessage={showMessage}
        rsSkillsGroup={rsSkillsGroup}
        rsSpecificKnowledge={rsSpecificKnowledge}
        rsSpecificSkills={rsSpecificSkills}
      />
    </Layout>
  );
};

export default Curricula;
